<script lang="ts">
	import type { Post } from '$lib/api/rpc';
	import { getHubStores } from '$lib/stores/hubStores';
	import { page } from '$app/stores';
	import Context from '$lib/components/Post/Tile/Context.svelte';
	import TitleImageContainer from './TitleImage.svelte';
	import Summary from './Summary.svelte';
	import Tag from '$lib/components/Tag/Tag.svelte';
	import Footer from '$lib/components/Post/Footer/Footer.svelte';
	import Header from '$lib/components/Post/Header/Header.svelte';
	import Wrapper from './Wrapper.svelte';
	import { _ } from 'svelte-i18n';
	import type { PostHeaderParent } from '$lib/components/Post/OptionsMenu/getOptionsMenuPerms';
	import { setPostContextStore } from '$lib/components/Post/postContextStore';
	import { getQuickPollURL } from '$lib/utils/getQuickPollURL';
	import { toastMessages } from '$lib/stores/toastMessageStore';

	export let post: Post;
	export let postIndex: number | undefined = undefined;
	export let parent: PostHeaderParent;

	let quickPollUrl: string | undefined;

	const inCarousel = parent === 'carousel';
	const hasImage = post.title_image?.url;

	const { hub, user, api } = getHubStores();

	/**
	 * @description The post children are not returned in the getPosts response,
	 * so we need to fetch the post to get the widget url if we know there is a widget
	 * in the post. Ideally we would have the widget url in the getPosts response.
	 */
	const fetchQuickPollUrl = async () => {
		try {
			const resp = await api.getPost({ postId: post.id, hubId: $hub.id });

			const postChildren = resp.post?.children ?? [];
			const widgetBlock = postChildren.find((block) => block.type === 'widget');

			if (widgetBlock) {
				const widgetUrl = widgetBlock.etc.widget?.url;

				if (widgetUrl) {
					quickPollUrl = getQuickPollURL({
						quickPollUrl: widgetUrl,
						hub: $hub,
						user: $user,
					});
				}
			}
		} catch (e) {
			toastMessages.danger($_('common.error.wrong'));
		}
	};

	setPostContextStore(post);

	$: hasWidget = post.content_hints.includes('widget');
	$: target = $hub?.settings.open_new_tab ? '_blank' : '_self';
	$: {
		if (hasWidget) {
			fetchQuickPollUrl();
		} else {
			quickPollUrl = undefined;
		}
	}
</script>

<Wrapper {parent}>
	<div class="wrapper" class:inCarousel class:hasImage>
		<Header {postIndex} {parent} />
		{#if post.context}
			<Context {target} />
		{/if}
		{#if post.title_image?.url}
			<div class="titleImageWrapper" class:inCarousel>
				<TitleImageContainer {target} {parent} />
			</div>
		{/if}
		{#if hasWidget && quickPollUrl}
			<div>
				<iframe src={quickPollUrl} title="QuickPoll" class="quickPoll" />
			</div>
		{/if}
		<Summary {target} />
		{#if post.tags && post.tags.length > 0}
			<div class="tag-container" class:inCarousel>
				{#each post.tags as tag}
					<Tag to={`/${$page.params.hubPrefix}?tag=${tag}`}>
						{tag}
					</Tag>
				{/each}
			</div>
		{/if}
		<div style="flex: 1;" />
		<div class="footer">
			<Footer />
		</div>
	</div>
</Wrapper>

<style>
	.wrapper {
		display: flex;
		position: relative;
		flex-direction: column;
		block-size: 100%;
		inline-size: 100%;
	}

	.wrapper.inCarousel {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: auto 1fr auto;
		aspect-ratio: 3.24 / 1;
		/* to be able to keep the fixed height for featured posts that are in carousel with and without images */
	}

	.wrapper.inCarousel.hasImage {
		grid-template-columns: 1fr 1fr;
	}

	.titleImageWrapper.inCarousel {
		grid-row: 1 / 6;
		order: -1;
	}

	.tag-container {
		padding-block: var(--gutterSmaller) var(--gutterSmall);
		padding-inline: var(--gutterMedium);
	}

	.tag-container.inCarousel {
		position: absolute;
		inset-block-end: 50px;
		inset-inline-end: 50px;
	}

	.titleImageWrapper {
		border-block: rgb(0 0 0 / 5%) solid 1px;
	}

	.footer {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: var(--gutterMedium);
		background-color: rgb(0 0 0 / 1%);
		border-block-start: rgb(0 0 0 / 5%) solid 1px;
	}

	.quickPoll {
		block-size: 400px;
		inline-size: 100%;
		border: 0;
	}
</style>
